import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraAssistPanel, SpecraButton, SpecraIconButton, SpecraLogo } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Brain, Expand, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/authContext';
import { useChatScroll } from '../hooks/useChatScroll';
import { useChatHistory } from '../hooks/useConversation';
import { useDrawerResize } from '../hooks/useDrawerResize';
import { useMemories } from '../hooks/useMemories';
import { useMemoryDeepLink } from '../hooks/useMemoryDeepLink';
import { useShanksChatConnect } from '../hooks/useShanksChatConnect';
import { assistConversationId, envelopeChip, expandDestination, parseChatOrigin, resolveAssistPath } from '../lib/chat/envelope';
import { memoryScopesFromEnvelope } from '../lib/chat/memoryScope';
import { shanksConversationId as toShanksConversationId } from '../lib/chat/shanks';
import { useLayoutStore } from '../lib/stores/LayoutStore';
import { ChatHistoryUnavailable, ChatTurnError } from './ChatView/ChatRecovery';
import { ChatThread } from './ChatView/ChatThread';
import { ShareButton } from './ChatView/ShareButton';
import { convertShanksToChatMessages, mergeMessagesById } from './ChatView/shanksMessageAdapter';
import { useQueuedChatFeedback } from './ChatView/useQueuedChatFeedback';
import { ComposerInput } from './ComposerInput';
import { MemoryPanel } from './MemoryPanel';
const ASSIST_RAIL_MIN = 320;
const ASSIST_RAIL_MAX = 960;
/** The most of the window the rail may take. */ const ASSIST_RAIL_SHARE = 0.45;
/** The rail's width before anyone drags it: 27% of the window, 320–440px. */ export function assistRailDefaultWidth(viewportWidth) {
    return Math.round(Math.min(440, Math.max(ASSIST_RAIL_MIN, viewportWidth * 0.27)));
}
/**
 * Starter questions for where the rail was opened. Each one has an answer on
 * any program, even an empty one ("nothing is overdue" is an answer); a
 * question about a record that may not exist (an audit, a named CLIN) sends
 * the assistant hunting for it, so none is offered.
 */ export function assistSuggestions(surface) {
    switch(surface){
        case 'delivery_program':
            return [
                'What needs my attention on this program?',
                'What is due in the next two weeks?',
                'Is anything overdue?'
            ];
        case 'delivery_home':
            return [
                'What needs my attention this week?',
                'Which programs have overdue deliverables?',
                'What is due in the next two weeks?'
            ];
        case 'capture_pursuit':
            return [
                'Summarize this pursuit',
                'Who is the likely incumbent?',
                'What should we do next?'
            ];
        default:
            return [
                'What needs my attention this week?',
                'What is due in the next two weeks?',
                'Summarize a document I upload'
            ];
    }
}
/** The widest the rail goes in this window, as the resize hook clamps it. */ export function assistRailMaxWidth(viewportWidth) {
    return Math.max(ASSIST_RAIL_MIN, Math.min(ASSIST_RAIL_MAX, Math.floor(viewportWidth * ASSIST_RAIL_SHARE)));
}
/**
 * The app-wide Assist rail: a shanks chat docked to the right edge of the shell
 * (same primitives as AISidebarShell — ChatThread + ComposerInput). It mounts
 * in the app shell and persists across navigation. Bind changes remount the
 * session so the same program/pursuit resumes the same conversation id.
 * "Open in full view" hands off to the workspace for wherever the chat
 * originated; ⌘J / close hides it.
 */ export function AssistRail({ onClose }) {
    const location = useLocation();
    const assistOrigin = useLayoutStore((s)=>s.assistOrigin);
    const pinned = useLayoutStore((s)=>s.assistChatId);
    const setPinned = useLayoutStore((s)=>s.setAssistChatId);
    const { user } = useAuth();
    const livePath = `${location.pathname}${location.search}`;
    // A pinned conversation is the chat the user brought with them; it keeps
    // the scope of the page it came from (its program or project), recorded as
    // assistOrigin when it was pinned. Otherwise the rail follows the route.
    const originPath = pinned ? assistOrigin || '/assistant' : resolveAssistPath(livePath, assistOrigin);
    const envelope = useMemo(()=>parseChatOrigin(originPath), [
        originPath
    ]);
    const chatId = pinned ?? assistConversationId(envelope, user?.id);
    const close = useCallback(()=>{
        setPinned(null);
        onClose();
    }, [
        onClose,
        setPinned
    ]);
    return /*#__PURE__*/ _jsx(AssistRailSession, {
        chatId: chatId,
        existing: !!pinned,
        envelope: envelope,
        originPath: originPath,
        onClose: close
    }, chatId);
}
function AssistRailSession({ chatId, existing, envelope, originPath, onClose }) {
    const navigate = useNavigate();
    const history = useChatHistory(chatId, !existing && sessionStorage.getItem(`assist-started::${chatId}`) !== 'true');
    const { scrollRef, awayFromBottom, jumpToLatest } = useChatScroll(chatId);
    // Resizable from its left edge; its own remembered width, not the
    // document drawer's. Until dragged it takes a share of the window, so a
    // laptop keeps most of the screen for the page.
    const [defaultWidth] = useState(()=>assistRailDefaultWidth(window.innerWidth));
    const [width, setWidth] = useState(defaultWidth);
    const resizeHandleRef = useDrawerResize({
        storageKey: 'assist-rail-width',
        minWidth: ASSIST_RAIL_MIN,
        maxWidth: ASSIST_RAIL_MAX,
        maxViewportFraction: ASSIST_RAIL_SHARE,
        defaultWidth,
        onWidthChange: setWidth
    });
    const rememberConversation = useCallback(()=>sessionStorage.setItem(`assist-started::${chatId}`, 'true'), [
        chatId
    ]);
    const savedSubmission = history.data?.submissions?.find((s)=>s.state === 'running') ?? history.data?.submissions?.[0];
    const restoredSubmission = useMemo(()=>savedSubmission?.message ? {
            id: savedSubmission.submissionId,
            content: savedSubmission.message,
            fileIds: savedSubmission.fileIds,
            state: savedSubmission.state
        } : undefined, [
        savedSubmission
    ]);
    const shanks = useShanksChatConnect({
        chatId,
        // Same server id ChatView uses: "shanks-<uuid>" is the route form.
        initialConversationId: toShanksConversationId(chatId) ?? chatId,
        projectId: envelope.projectId,
        context: envelope,
        onConversationId: rememberConversation,
        onFinish: ()=>void history.refetch(),
        onError: ()=>void history.refetch(),
        restoredSubmission
    });
    const responseBusy = shanks.isLoading || !history.data?.readOnly && savedSubmission?.state === 'running';
    const unfinished = !history.data?.readOnly && savedSubmission && [
        'incomplete',
        'failed',
        'interrupted'
    ].includes(savedSubmission.state);
    const incomplete = shanks.incomplete || !shanks.error && unfinished && savedSubmission.state === 'incomplete';
    const turnError = responseBusy ? null : shanks.error ?? (unfinished ? incomplete ? 'This request needs more work. Your progress is saved. Continue to finish it.' : 'This response was interrupted. Retry your saved request.' : null);
    const messages = useMemo(()=>mergeMessagesById(history.data?.messages ?? [], convertShanksToChatMessages(shanks.messages), shanks.isLoading), [
        history.data?.messages,
        shanks.messages,
        shanks.isLoading
    ]);
    const chip = envelopeChip(envelope);
    // Same memory as the full-page chat: notes for this page's scope, and
    // "Remember this" from the composer opens the panel with the draft.
    const memoryScopes = useMemo(()=>memoryScopesFromEnvelope(envelope), [
        envelope
    ]);
    const memories = useMemories(memoryScopes);
    const [memoryOpen, setMemoryOpen] = useState(false);
    const [memoryTab, setMemoryTab] = useState();
    const openMemoryTab = useCallback((tab)=>{
        setMemoryTab(tab);
        setMemoryOpen(true);
    }, []);
    useMemoryDeepLink(openMemoryTab);
    const [memoryDraft, setMemoryDraft] = useState();
    const rememberDraft = useCallback((text)=>{
        const trimmed = text.trim();
        if (!trimmed) return;
        const firstLine = trimmed.split('\n')[0]?.trim() ?? 'Note';
        setMemoryDraft({
            title: firstLine.length > 80 ? `${firstLine.slice(0, 77)}...` : firstLine,
            body: trimmed
        });
        setMemoryOpen(true);
    }, []);
    // Shareable once the conversation exists on the server.
    const persisted = !!shanks.conversationId || (history.data?.messages?.length ?? 0) > 0;
    // Accept/Reject/Comment on a card in the rail continues this conversation.
    // Queue while the prior turn is still running so [delivery-action] lines
    // are never dropped (OpenAI turns can hang for minutes).
    const sendCardFeedback = useQueuedChatFeedback((message)=>{
        if (history.isError) return;
        return shanks.sendMessage(message);
    }, responseBusy || history.isLoading, {
        disabled: history.isError
    });
    const handleSubmit = async (data)=>{
        const content = data.prompt.trim();
        if (!content || responseBusy || history.isLoading || history.isError) return;
        const fileIds = data.attachments?.map((a)=>a.id).filter((id)=>!!id);
        await shanks.sendMessage(content, {
            fileIds
        });
    };
    const suggestions = assistSuggestions(envelope.surface);
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative flex min-h-0 xl:h-full",
        style: {
            '--component-assist-panel-width': `${width}px`
        },
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: resizeHandleRef,
                role: "separator",
                tabIndex: 0,
                "aria-orientation": "vertical",
                "aria-label": "Resize Assist panel",
                "aria-valuenow": width,
                "aria-valuemin": ASSIST_RAIL_MIN,
                "aria-valuemax": assistRailMaxWidth(window.innerWidth),
                title: "Drag or use the arrow keys to resize",
                "data-testid": Testids.assistant.railResizeHandle,
                className: "absolute inset-y-0 -left-1 z-10 hidden w-2 cursor-col-resize touch-none bg-transparent transition-colors hover:bg-border focus-visible:bg-border focus-visible:outline-none xl:block"
            }),
            /*#__PURE__*/ _jsxs(SpecraAssistPanel, {
                transcriptRef: scrollRef,
                composer: history.isLoading || history.isError ? undefined : /*#__PURE__*/ _jsx(ComposerInput, {
                    dense: true,
                    mode: "chat",
                    onSubmit: handleSubmit,
                    placeholder: "Ask Specra…",
                    submitLabel: "Send",
                    submitting: responseBusy,
                    onStop: shanks.stop,
                    onRemember: rememberDraft,
                    rememberDisabled: responseBusy || memories.isLoading,
                    quickToggle: true,
                    attachmentChatId: shanks.conversationId ?? chatId.replace(/^shanks-/, '')
                }),
                headerActions: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                            "data-testid": Testids.memory.openButton,
                            label: "Open memory notes",
                            onClick: ()=>setMemoryOpen(true),
                            size: "sm",
                            variant: "ghost",
                            children: /*#__PURE__*/ _jsx(Brain, {
                                "aria-hidden": "true"
                            })
                        }),
                        persisted && !history.data?.readOnly ? /*#__PURE__*/ _jsx(ShareButton, {
                            chatId: chatId,
                            chatTitle: chip ? `Assist · ${chip}` : 'Assist chat'
                        }) : null,
                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                            label: "Open in full view",
                            onClick: ()=>{
                                const dest = expandDestination(originPath, shanks.conversationId ?? chatId);
                                if (dest === '/assistant' && envelope.surface !== 'root' && envelope.surface !== 'assistant') {
                                    navigate('/assistant', {
                                        state: {
                                            seedPins: [
                                                envelope
                                            ]
                                        }
                                    });
                                    return;
                                }
                                navigate(dest);
                            },
                            size: "sm",
                            variant: "ghost",
                            children: /*#__PURE__*/ _jsx(Expand, {
                                "aria-hidden": "true"
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                            label: "Close (⌘J)",
                            onClick: onClose,
                            size: "sm",
                            variant: "ghost",
                            children: /*#__PURE__*/ _jsx(X, {
                                "aria-hidden": "true"
                            })
                        })
                    ]
                }),
                title: chip ? /*#__PURE__*/ _jsxs("span", {
                    className: "flex min-w-0 items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            children: "Assist"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "truncate rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-normal text-muted-foreground",
                            children: chip
                        })
                    ]
                }) : 'Assist',
                children: [
                    history.isLoading ? /*#__PURE__*/ _jsx("output", {
                        children: "Loading conversation…"
                    }) : null,
                    history.isError ? /*#__PURE__*/ _jsx(ChatHistoryUnavailable, {
                        error: history.error,
                        onRetry: ()=>void history.refetch()
                    }) : null,
                    history.isLoading || history.isError ? null : messages.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-[9px]",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-start gap-[9px]",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "mt-px flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[6px] bg-muted",
                                        children: /*#__PURE__*/ _jsx(SpecraLogo, {
                                            variant: "mark",
                                            size: 12,
                                            label: ""
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "min-w-0 flex-1 pt-0.5 text-[12.5px] leading-[1.6] text-foreground",
                                        children: "Ask about anything on your screen — programs, deliverables, documents, or the work ahead."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex flex-wrap gap-[6px] pl-[31px]",
                                children: suggestions.map((q)=>/*#__PURE__*/ _jsx("button", {
                                        type: "button",
                                        onClick: ()=>void shanks.sendMessage(q),
                                        disabled: responseBusy || history.isLoading || history.isError,
                                        className: "inline-flex items-center rounded-full border border-border bg-card px-[11px] py-[5px] text-[11.5px] text-foreground transition-colors hover:border-muted-foreground disabled:opacity-60",
                                        children: q
                                    }, q))
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(ChatThread, {
                        chatId: chatId,
                        isSending: responseBusy,
                        messages: messages,
                        variant: "assist",
                        onFeedback: sendCardFeedback,
                        // Already beside the page: a link just moves the page.
                        onOpenAppLink: navigate
                    }),
                    turnError ? /*#__PURE__*/ _jsx(ChatTurnError, {
                        error: turnError,
                        incomplete: incomplete,
                        onRetry: ()=>void shanks.retry()
                    }) : null,
                    awayFromBottom ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        className: "sticky bottom-3 z-10",
                        onClick: jumpToLatest,
                        children: "Jump to latest"
                    }) : null
                ]
            }),
            /*#__PURE__*/ _jsx(MemoryPanel, {
                envelope: envelope,
                isOpen: memoryOpen,
                onClose: ()=>{
                    setMemoryOpen(false);
                    setMemoryTab(undefined);
                },
                initialTab: memoryTab,
                draft: memoryDraft,
                onDraftConsumed: ()=>setMemoryDraft(undefined)
            })
        ]
    });
}
